import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { api } from '../../services/api';
import { useTheme, alpha } from '@mui/material/styles';
import { GroupOutlined } from '@mui/icons-material';
import SearchComponent from '../../components/Search';
import { filterRowsByColumns } from '../../Utils/gridSearch';
import StatusFilterHeader, { applyStatusFilter } from '../../components/StatusFilterHeader';
import { useToast } from "../../components/ToastContext";
import CreateUser from '../adminSettings/CreateUser';

const ListUser = () => {
  const theme = useTheme();
  const toast = useToast();
  const userId = sessionStorage.getItem('userId');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [confirmStatusDialogOpen, setConfirmStatusDialogOpen] = useState(false);
  const [userToChangeStatus, setUserToChangeStatus] = useState(null);
  const [switchToActive, setSwitchToActive] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/Account/manageUsers/${userId}/0`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [userId]);

  const handleAddNew = () => {
    setSelectedUserData(null);
    setIsUserDialogOpen(true);
  };

  const handleEdit = (userRow) => {
    setSelectedUserData(userRow);
    setIsUserDialogOpen(true);
  };

  const handleDelete = async (targetUserId) => {
    setLoading(true);
    try {
      const response = await api.post(`/Account/deleteUser/${targetUserId}`, {
        params: { requesterId: userId }
      });
      if (response.status === 200) {
        setUsers(users.filter((user) => user.userId !== targetUserId));
        toast.success('User deleted successfully!');
      } else {
        toast.error('Failed to delete user.');
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while deleting the user.');
    } finally {
      setLoading(false);
      setConfirmDeleteOpen(false);
      setUserToDelete(null);
    }
  };

  const handleStatusChange = async (user, isActive) => {
    try {
      const payload = { ...user, isActive, requester: userId };
      const response = await api.post(`/Account/UpdateUserDetails/${user.userId}`, payload);

      if (response.status === 200) {
        setUsers((prevUsers) => prevUsers.map((u) => (u.userId === user.userId ? { ...u, isActive } : u)));
        toast.success("User status updated successfully.");
      } else {
        toast.error("Failed to update user status.");
      }
    } catch (error) {
      const apiErrorMessage = error.response?.data?.errorMessages?.[0]?.message 
                           || error.response?.data?.message;
      
      if (apiErrorMessage) {
        toast.error(apiErrorMessage);
      } else {
        toast.error(error.message || "Failed to update user status.");
      }
    } finally {
      setConfirmStatusDialogOpen(false);
      setUserToChangeStatus(null);
    }
  };

  const columns = useMemo(() => [
    { field: 'name', headerName: 'User Name', flex: 0.8 },
    { field: 'gender', headerName: 'Gender', flex: 0.6 },
    { field: 'userName', headerName: 'Email', flex: 1 },
    { field: 'organizationName', headerName: 'Organization Name', flex: 1 },
    // { field: 'roleName', headerName: 'Role Name', flex: 1 },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 0.8,
      // Expose a searchable label so search matches "active"/"inactive"
      valueGetter: (value, row) => (row?.isActive ? 'Active' : 'Inactive'),
      renderHeader: () => (
        <StatusFilterHeader value={statusFilter} onChange={setStatusFilter} />
      ),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            checked={params.row.isActive}
            onChange={() => {
              setUserToChangeStatus(params.row);
              setSwitchToActive(!params.row.isActive);
              setConfirmStatusDialogOpen(true);
            }}
            color="primary"
          />
          <Box sx={{ paddingLeft: 1, fontWeight: 600, color: params.row.isActive ? '#388e3c' : '#d32f2f' }}>
            {params.row.isActive ? 'Active' : 'Inactive'}
          </Box>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          sx={{ mt: 0.9, borderRadius: 999, px: 1.8 }}
          onClick={() => handleEdit(params.row)}
        >
          Edit
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [theme, statusFilter]);

  // Search across all data columns, then apply the Status dropdown filter
  const filteredUsers = useMemo(
    () => applyStatusFilter(filterRowsByColumns(users, searchTerm, columns), statusFilter),
    [users, searchTerm, columns, statusFilter]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1), color: theme.palette.primary.main }}>
            <GroupOutlined />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              Users
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SearchComponent
            value={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search users..."
            sx={{ mb: theme.spacing(2) }}
          />
          <Button
            variant="contained"
            onClick={handleAddNew}
            sx={{
              color: theme.palette.common.white,
              bgcolor: theme.palette.primary.main,
              borderRadius: 2,
              px: 2.4,
              py: 1.1,
            }}>
            Add New User
          </Button>
        </Box>
      </Box>

      <Box sx={{ height: 'calc(100vh - 310px)', minHeight: 380, width: '100%' }}>
        {loading ? (
          <Box sx={{ minHeight: 280, display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            getRowId={(row) => row.userId}
            disableSelectionOnClick
            hideFooterSelectedRowCount
            sx={{
              border: "none",
              bgcolor: (t) => t.palette.mode === "dark" ? "background.paper" : "#ffffff",

              "& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root, & .MuiCheckbox-root.MuiCheckbox-indeterminate .MuiSvgIcon-root, & .MuiCheckbox-root.Mui-indeterminate .MuiSvgIcon-root": {
                color: (t) => `${t.palette.primary.main} !important`,
              },

              "& .MuiCheckbox-root .MuiSvgIcon-root": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)",
              },

              "& .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root": {
                color: (t) => `${t.palette.action.disabled} !important`,
              },

              "--DataGrid-rowBorderColor": "transparent",

              "& .MuiDataGrid-filler": { display: "none" },

              "& .MuiDataGrid-columnHeaders": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                color: "text.primary",
                borderBottom: "none",
              },

              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold !important",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              },

              "& .MuiDataGrid-columnSeparator": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
              },

              "& .MuiDataGrid-columnHeader .MuiSvgIcon-root:not(.MuiCheckbox-root .MuiSvgIcon-root)": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
              },

              "& .MuiDataGrid-cell": {
                color: "text.primary",
                borderBottom: "none !important",
              },

              "& .MuiDataGrid-row:nth-of-type(even)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "#fafafa",
              },

              "& .MuiDataGrid-row:nth-of-type(odd)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "transparent" : "#ffffff",
              },

              "& .MuiDataGrid-row:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08) !important" : "#e8f0fe !important",
              },

              "& .MuiDataGrid-row.Mui-selected": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.16) !important" : "#e3f2fd !important",
              },

              "& .MuiDataGrid-row.Mui-selected:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.24) !important" : "#d0eafb !important",
              },

              "& .MuiDataGrid-footerContainer": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                borderTop: "none",
                color: "text.primary",
              },

              "& .floating-actions, & .row-actions": {
                backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(40, 40, 40, 0.9)" : "#e8f0fe",
                flex: 1,
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                gap: "6px",
                opacity: 0,
                pointerEvents: "none",
                transition: "opacity 0.2s ease-in-out",
                padding: "4px 8px",
                borderRadius: "4px",
              },

              "& .MuiDataGrid-row:hover .floating-actions, & .MuiDataGrid-row:hover .row-actions, & .MuiDataGrid-row.Mui-selected .floating-actions, & .MuiDataGrid-row.Mui-selected .row-actions": {
                opacity: 1,
                pointerEvents: "auto",
              },

              "& .MuiDataGrid-cell[data-field='__floating_actions'], & .MuiDataGrid-cell[data-field='actions']": {
                overflow: "visible !important",
                width: "0px !important",
                padding: 0,
                borderBottom: "none !important",
              },

              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                outline: "none"
              },
            }}
          />
        )}
      </Box>

      {/* External Dialog for Create/Edit User */}
      <CreateUser
        open={isUserDialogOpen}
        onClose={() => setIsUserDialogOpen(false)}
        userData={selectedUserData}
        onSuccess={fetchUsers}
      />

      {/* Confirmation Dialogs for Status & Delete */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} >
        <DialogTitle sx={{ fontSize: '20px', fontWeight: 700 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} color="primary" variant="outlined">Cancel</Button>
          <Button
            onClick={() => handleDelete(userToDelete)}
            variant="contained"
            sx={{ bgcolor: '#ffebee', color: '#d32f2f', border: '1px solid #d32f2f' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmStatusDialogOpen} onClose={() => setConfirmStatusDialogOpen(false)}>
        <DialogTitle sx={{ fontSize: '20px', fontWeight: 700 }}>Confirm Status Change</DialogTitle>
        <DialogContent>
          Are you sure you want to {switchToActive ? 'activate' : 'deactivate'} this user?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleStatusChange(userToChangeStatus, switchToActive)} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListUser;